import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { IndividualService } from 'app/services/individuals/individual.service';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';

interface IAffiliationOption {
  id: number;
  name: string;
  type: 'ATP' | 'Firm';
  isPrivate: boolean;
}

@Component({
  selector: 'app-individual-add-affiliation-dialog',
  templateUrl: './individual-add-affiliation-dialog.component.html',
  styleUrls: ['./individual-add-affiliation-dialog.component.scss']
})
export class IndividualAddAffiliationDialogComponent implements OnInit {
  public uiData = LEAD_BASED_PAINT;
  public loading = false;
  public options: IAffiliationOption[] = [];
  public filteredOptions: IAffiliationOption[] = [];
  public selectedOption: IAffiliationOption | string | null = null;

  constructor(
    private readonly dialogRef: MatDialogRef<IndividualAddAffiliationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { existingAffiliations: Array<{ id: number; type: string }> },
    private readonly individualService: IndividualService,
    private readonly toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.loadOptions();
  }

  loadOptions(): void {
    this.loading = true;
    forkJoin({
      atps: this.individualService.getAllAffiliationAtps(),
      firms: this.individualService.getAllAffiliationFirms(),
    }).subscribe({
      next: ({ atps, firms }) => {
        const existingAffiliationKeys = new Set(
          (this.data?.existingAffiliations || []).map((affiliation) => `${(affiliation.type || '').toUpperCase()}:${affiliation.id}`)
        );

        const atpOptions = (atps || [])
          .map((atp) => ({
            id: atp.id,
            name: atp.name,
            type: 'ATP' as const,
            isPrivate: atp.privateCompany === true,
          }))
          .filter((atp) => !existingAffiliationKeys.has(`ATP:${atp.id}`));

        // Keep private firms visible in options; do not filter by isPrivate.
        const firmOptions = (firms || [])
          .map((firm) => ({
            id: firm.id,
            name: firm.name,
            type: 'Firm' as const,
            isPrivate: firm.isPrivate === true,
          }))
          .filter((firm) => !existingAffiliationKeys.has(`FIRM:${firm.id}`));

        this.options = [...atpOptions, ...firmOptions].sort((left, right) => {
          const typeCompare = left.type.localeCompare(right.type);
          if (typeCompare !== 0) {
            return typeCompare;
          }

          return left.name.localeCompare(right.name);
        });

        this.filteredOptions = [...this.options];
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.toastr.error('Failed to load affiliation options: ' + (error?.message || error?.error || 'Unknown error'));
      }
    });
  }

  updateSearch(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = (input?.value || '').trim().toLowerCase();
    this.selectedOption = null;

    this.filteredOptions = this.options.filter((option) => {
      if (!value) {
        return true;
      }

      return [option.type, option.id, option.name]
        .join(' ')
        .toLowerCase()
        .includes(value);
    });
  }

  displayOption(option: IAffiliationOption | string | null): string {
    if (!option) {
      return '';
    }

    if (typeof option === 'string') {
      return option;
    }

    return `${option.type}: ${option.name}`;
  }

  isOptionSelected(): boolean {
    return !!this.selectedOption && typeof this.selectedOption !== 'string';
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onSubmit(): void {
    if (!this.isOptionSelected()) {
      this.toastr.error('Please select an affiliation option from the list.');
      return;
    }

    this.dialogRef.close(this.selectedOption as IAffiliationOption);
  }
}
